import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import { Pencil } from 'lucide-react'
import { copy } from '../../copy/investor-copy'
import { formatShortDate } from '../following/dates'
import { isLargestPlan, planName } from './plan-name'
import { primaryAction } from './primary-action'
import { QuoteBlock } from './quote-block'
import { RelCard } from './rel-card'
import { SourceTag } from './source-tag'
import { StatusChip } from './status-chip'
import type { CardActions, CardVariant, Relationship } from './types'

const LABEL = 'mt-3 text-xs font-medium uppercase tracking-wider text-muted-foreground'
const TEXT_LINK = 'text-xs text-muted-foreground transition-colors hover:text-foreground'

/** The follow's condition, or "Material updates" for a follow with none. */
function conditionText(data: Relationship): string {
  return data.follow?.condition ?? copy('card.following.materialUpdates')
}

function EditFollow({ onEdit }: { onEdit: (anchor: Element) => void }) {
  return (
    <SecondaryButton size="sm" icon={<Pencil />} onClick={(e) => onEdit(e.currentTarget)}>
      {copy('card.following.edit')}
    </SecondaryButton>
  )
}

/** "Following for": the follow as the investor set it, in every state a follow can be in. */
export function FollowingForCard({
  data,
  variant,
  actions,
}: {
  data: Relationship
  variant: CardVariant
  actions: CardActions
}) {
  const { state, follow } = data
  if (state === 'NONE' || !follow) return null
  const action = primaryAction({
    state,
    variant,
    hasProfileLink: data.startup.profileLinkToken !== null,
  })

  if (state === 'ENDED_BY_FOUNDER') {
    return (
      <RelCard
        title={copy('card.ended.title')}
        right={<StatusChip state={state} variant={variant} />}
      >
        <p className="mt-1 text-sm text-foreground/85">
          {copy('card.ended.body', {
            startup: data.startup.name,
            date: formatShortDate(follow.endedAt ?? follow.followedAt),
          })}
        </p>
        {action === 'back-to-following' && (
          <CtaButton size="md" fullWidth className="mt-3" onClick={actions.onBackToFollowing}>
            {copy('card.ended.back')}
          </CtaButton>
        )}
      </RelCard>
    )
  }

  const since = copy('card.following.since', { date: formatShortDate(follow.followedAt) })

  if (state === 'WAITING_CHECK' || state === 'WAITING_PLACE') {
    const largest = isLargestPlan(data.allowance.plan)
    return (
      <RelCard
        title={copy('card.following.title')}
        right={<StatusChip state={state} variant={variant} />}
      >
        <p className="mt-2 text-sm text-foreground">{conditionText(data)}</p>
        <p className={cn(LABEL, 'mt-3')}>
          {state === 'WAITING_CHECK' ? copy('card.waitCheck.label') : copy('card.waitPlace.label')}
        </p>
        <p className="mt-1 text-sm text-foreground/85">
          {state === 'WAITING_CHECK'
            ? copy('card.waitCheck.body')
            : copy(largest ? 'card.waitPlace.bodyLargest' : 'card.waitPlace.body', {
                used: data.allowance.used,
                limit: data.allowance.limit,
                plan: planName(data.allowance.plan),
              })}
        </p>
        {action === 'see-plans' && !largest && (
          <CtaButton size="md" fullWidth className="mt-3" onClick={actions.onSeePlans}>
            {copy('card.waitPlace.cta')}
          </CtaButton>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <EditFollow onEdit={actions.onEditFollow} />
          <button type="button" className={TEXT_LINK} onClick={actions.onCancelFollow}>
            {copy('card.waiting.cancel')}
          </button>
        </div>
      </RelCard>
    )
  }

  const met = state === 'CONDITION_MET' ? follow.conditionMet : null
  return (
    <RelCard
      title={copy('card.following.title')}
      right={<StatusChip state={state} variant={variant} />}
    >
      <p className="mt-2 text-sm text-foreground">{conditionText(data)}</p>
      {met && (
        <>
          <p className={LABEL}>{copy('card.met.posted')}</p>
          <div className="mt-1">
            <QuoteBlock>{`“${met.quote}”`}</QuoteBlock>
          </div>
          <div className="mt-2">
            <SourceTag>
              {copy('card.met.source', {
                label: met.source,
                date: formatShortDate(met.date),
                added: formatShortDate(met.addedAt),
              })}
            </SourceTag>
          </div>
          {variant === 'panel' ? (
            <SecondaryButton size="md" fullWidth className="mt-3" onClick={actions.onOpenUpdate}>
              {copy('card.met.open')}
            </SecondaryButton>
          ) : (
            action === 'open-update' && (
              <CtaButton size="md" fullWidth className="mt-3" onClick={actions.onOpenUpdate}>
                {copy('card.met.open')}
              </CtaButton>
            )
          )}
          <p className="mt-2 text-center">
            <button type="button" className={TEXT_LINK} onClick={actions.onAllUpdates}>
              {copy('card.met.all')}
            </button>
          </p>
          <p className="mt-2 text-xs text-muted-foreground">{copy('card.met.continues')}</p>
        </>
      )}
      <div className="mt-3 flex items-center justify-between gap-3">
        <EditFollow onEdit={actions.onEditFollow} />
        <span className="text-xs text-muted-foreground">{since}</span>
      </div>
    </RelCard>
  )
}
